<template>
  <form class="tools-registration-form" autocomplete="off" >
    <div class="tools-registration-form__row">
      <div class="tools-registration-form__col tools-registration-form__col--full">
        <label class="control--label" for="serial-firm">{{ $t('sn-registration.enterSerialLabel') }}</label>
        <div class="tools-registration-form__col--flex tools-registration-form__col--full">
          <the-mask
            v-model="formData.serialFirm"
            id="serial-firm"
            placeholder="WHE"
            mask="AAAA"
            maxlength="4"
            class="control control--input control--serial-firm"
            :class="{'control--error': $v.formData.serialFirm.$error}"
            @blur.native="$v.formData.serialFirm.$touch"
            :disabled="isSerialSubmitted"
          ></the-mask>
          <the-mask
            v-model="formData.serialNum"
            id="serial-num"
            placeholder="091707-01001"
            mask="######-F######"
            maxlength="14"
            :tokens="serialTokens"
            class="control control--input control--serial-num"
            :class="{'control--error': $v.formData.serialNum.$error}"
            @blur.native="$v.formData.serialNum.$touch"
            :disabled="isSerialSubmitted"
          ></the-mask>
          <button
            v-if="!isSerialSubmitted"
            @click="submitSerial"
            type="button"
            class="control control--btn control--serial-btn"
          >
            {{ $t('sn-registration.btnNext') }}
          </button>
        </div>
      </div>
    </div>
    <template v-if="serialInfo && serialInfo.length === 1">
      <div class="tools-registration-form__row">
        <div class="tools-registration-form__col tools-registration-form__col--full">
          <span>{{ serialInfo[0].itemName }}</span>
        </div>
      </div>
    </template>
    <template v-else-if="serialInfo && serialInfo.length > 1">
      <div class="tools-registration-form__row">
        <div class="tools-registration-form__col tools-registration-form__col--full">
          <label class="control--label" for="select-tool">{{ $t('sn-registration.selectProduct') }}</label>
          <select-input
            @input="handleSelectTools"
            id="select-tool"
            :select-data="serialInfo"
            label="itemName"
            placeholder="Выберите инструмент"
          ></select-input>
        </div>
      </div>
    </template>
    <template v-if="isSerialSubmitted">
      <div class="tools-registration-form__row">
        <div class="tools-registration-form__col">
          <label class="control--label" for="name">{{ $t('sn-registration.firstNameLabel') }}</label>
          <input
            id="name"
            v-model.trim="formData.name"
            :placeholder="$t('sn-registration.firstNameLabel')"
            class="control control--input"
            :class="{'control--error': $v.formData.name.$error}"
            @blur="$v.formData.name.$touch"
          />
          <p class="control-box__help-block" :class="{ 'invalid dirty': $v.formData.name.$error }" v-if="$v.formData.name.$error">
            <template v-if="!$v.formData.name.required">
              {{ $t('blank_field_error') }}
            </template>
            <template v-else-if="!$v.formData.name.minLength">
              {{ $t('notfull_field_error') }}
            </template>
            <template v-else-if="!$v.formData.name.regRus">
              {{ $t('name_contains_rus_letters') }}
            </template>
            <template v-else-if="!$v.formData.name.regNum">
              {{ $t('field_contains_numbers') }}
            </template>
            <template v-else-if="!$v.formData.name.regSpace">
              {{ $t('field_contains_space') }}
            </template>
            <template v-else-if="!$v.formData.name.regSpec">
              {{ $t('field_contains_special_characters') }}
            </template>
            <template v-else-if="!$v.formData.name.regLatin">
              {{ $t('field_contains_latin_letter') }}
            </template>
          </p>
        </div>
        <div class="tools-registration-form__col">
          <label class="control--label" for="last-name">{{ $t('sn-registration.lastNameLabel') }}</label>
          <input
            id="last-name"
            v-model.trim="formData.lastName"
            :placeholder="$t('sn-registration.lastNameLabel')"
            class="control control--input"
            :class="{'control--error': $v.formData.lastName.$error}"
            @blur="$v.formData.lastName.$touch"
          />
          <p class="control-box__help-block" :class="{ 'invalid dirty': $v.formData.lastName.$error }" v-if="$v.formData.lastName.$error">
            <template v-if="!$v.formData.lastName.required">
              {{ $t('blank_field_error') }}
            </template>
            <template v-else-if="!$v.formData.lastName.minLength">
              {{ $t('notfull_field_error') }}
            </template>
            <template v-else-if="!$v.formData.lastName.regRus">
              {{ $t('name_contains_rus_letters') }}
            </template>
            <template v-else-if="!$v.formData.lastName.regNum">
              {{ $t('field_contains_numbers') }}
            </template>
            <template v-else-if="!$v.formData.lastName.regSpace">
              {{ $t('field_contains_space') }}
            </template>
            <template v-else-if="!$v.formData.lastName.regSpec">
              {{ $t('field_contains_special_characters') }}
            </template>
            <template v-else-if="!$v.formData.lastName.regLatin">
              {{ $t('field_contains_latin_letter') }}
            </template>
          </p>
        </div>
      </div>
      <div class="tools-registration-form__row">
        <div class="tools-registration-form__col tools-registration-form__col--full">
          <label class="control--label" for="phone">{{ $t('sn-registration.phoneLabel') }}</label>
          <phone-input
            id="phone"
            :value="formData.phone"
            disable-fields
          ></phone-input>
        </div>
      </div>
      <div class="tools-registration-form__row">
        <div class="tools-registration-form__col tools-registration-form__col--full">
          <label class="control--label" for="purchase-date">{{ $t('sn-registration.dateLabel') }}</label>
          <custom-date-picker
            id="purchase-date"
            v-model="formData.purchaseDate"
            :placeholder="$t('choose_date')"
            :lang="language"
            :append-to-body="false"
            format="YYYY-MM-DD"
            value-type="format"
            :disabled-date="disabledDates"
            :error="$v.formData.purchaseDate.$error"
            :show-error-msg="false"
          ></custom-date-picker>
          <p class="control-box__help-block" :class="{ 'invalid dirty': $v.formData.purchaseDate.$error }" v-if="$v.formData.purchaseDate.$error">
            <template v-if="!$v.formData.purchaseDate.required">
              {{ $t('blank_field_error') }}
            </template>
          </p>
        </div>
      </div>
      <div class="tools-registration-form__row">
        <div class="tools-registration-form__col tools-registration-form__col--full">
          <button @click="postFormData" type="button" class="control control--btn">{{ $t('sn-registration.registrationButton') }}</button>
        </div>
      </div>
      <div class="tools-registration-form__row">
        <div class="tools-registration-form__col tools-registration-form__col--full">
          <span class="agree-terms__text">
            {{ $t('agreement.text') }}
            <a href="/terms-and-conditions/" class="link agree-terms-link" target="_blank">{{ $t('agreement.link') }}</a>
          </span>
        </div>
      </div>
    </template>
    <custom-loader v-if="isSerialInfoLoading || isFormLoading" is-loading></custom-loader>
  </form>
</template>

<script>
import moment from 'moment'
import router from '@/router'
import api from '@/api'
import { mapState } from 'vuex'

import { maxLength, minLength, required } from 'vuelidate/lib/validators'
import { TheMask } from 'vue-the-mask'
import PhoneInput from '@/components/inputs/PhoneInput'
import CustomDatePicker from '@/components/inputs/CustomDatePicker'
import SelectInput from '@/components/inputs/SelectInput'
import CustomLoader from '@/components/helpers/Loader'

const regRus = value => !new RegExp(/[ъыёэ]/i).test(value)
const regLatin = value => !new RegExp(/[a-z]/i).test(value)
const regSpace = value => !new RegExp(/\s/g).test(value)
const regNum = value => !new RegExp(/\d/).test(value)
const regSpec = value => !new RegExp('[!@#№/$%^&*(),.?":;_=+{}|<>]').test(value)
// const isFirmInList = firms => value => firms.hasOwnProperty(value)

export default {
  name: 'ToolsRegistrationForm',
  components: { CustomLoader, SelectInput, CustomDatePicker, PhoneInput, TheMask },
  data () {
    return {
      firms: {},
      formData: {
        serialFirm: '',
        serialNum: '',
        serial: '',
        phone: '',
        name: '',
        email: '',
        lastName: '',
        purchaseDate: '',
        purchasePlace: 'other',
        typeId: 3
      },
      serialInfo: null,
      serialFullInfo: null,
      isSerialInfoLoading: false,
      isSerialSubmitted: false,
      isFormLoading: false
    }
  },
  computed: {
    ...mapState({
      language: state => state.translation.language,
      userFromServer: state => state.user
    }),
    isUserAuth () {
      return this.userFromServer?.auth
    },
    userData () {
      return this.userFromServer?.['user_data']
    },
    isValidForm () {
      return !this.$v.formData.$error && !this.$v.formData.$invalid
    },
    isValidSerial () {
      const { serialFirm, serialNum } = this.$v.formData

      return !serialFirm.$error && !serialFirm.$invalid && !serialNum.$error && !serialNum.$invalid
    },
    serialTokens () {
      return {
        'F': {
          pattern: /[0-9a-zA-Z]/,
          transform: v => v.toLocaleUpperCase()
        },
        '#': {
          pattern: /\d/
        }
      }
    }
  },
  validations () {
    return {
      formData: {
        serialFirm: {
          required,
          minLength: minLength(2),
          maxLength: maxLength(4)
          // isFirmInList: isFirmInList(this.firms)
        },
        serialNum: {
          required,
          minLength: minLength(6),
          maxLength: maxLength(14)
        },
        name: {
          required,
          minLength: minLength(2),
          regRus,
          regLatin,
          regSpace,
          regNum,
          regSpec
        },
        lastName: {
          required,
          minLength: minLength(2),
          regRus,
          regLatin,
          regSpace,
          regNum,
          regSpec
        },
        purchaseDate: {
          required
        }
      }
    }
  },
  methods: {
    disabledDates (date) {
      const startDay = new Date('2016-01-01')
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      startDay.setHours(0, 0, 0, 0)

      return date > today || date < startDay
    },
    getFirms () {
      api.serials.getFirms().then(res => {
        if (res?.length) {
          const result = {}

          res.forEach(item => {
            result[item.name] = item
          })

          this.firms = result
        }
      })
    },
    handleSelectTools (e) {
      this.serialInfo = [e]

      this.getSerialFullInfo(e.fullSerial).finally(() => {
        this.isSerialSubmitted = true
      })
    },
    getSerialInfo (serial) {
      return new Promise((resolve, reject) => {
        api.serials.getSerialInfo(serial)
          .then(res => {
            this.serialInfo = res
            resolve(res)
          })
          .catch(err => {
            reject(err)
          })
      })
    },
    getSerialFullInfo (fullSerial) {
      return new Promise((resolve, reject) => {
        api.serials.getSerialFullInfo(fullSerial)
          .then(res => {
            this.serialFullInfo = res
            resolve(res)
          })
          .catch(err => {
            reject(err)
          })
      })
    },
    touchSerialForm () {
      this.$v.formData.serialFirm.$touch()
      this.$v.formData.serialNum.$touch()
    },
    touchForm () {
      this.$v.formData.$touch()
    },
    submitSerial () {
      this.touchSerialForm()
      if (this.isValidSerial) {
        this.isSerialInfoLoading = true

        const serial = this.formData.serialFirm + this.formData.serialNum

        this.getSerialInfo(serial)
          .then(res => {
            if (res?.length === 1) {
              this.getSerialFullInfo(res[0].fullSerial).finally(() => {
                this.isSerialSubmitted = true
              })
            }
          })
          .catch(() => {
            this.formData.serial = this.formData.serialFirm + this.formData.serialNum
            this.isSerialSubmitted = true
          })
          .finally(() => {
            this.isSerialInfoLoading = false
          })
      }
    },
    clearFullForm () {
      this.$v.formData.$reset()
      this.serialInfo = null
      this.serialFullInfo = null
      this.isSerialInfoLoading = false
      this.isSerialSubmitted = false
      this.formData = {
        ...this.formData,
        serialFirm: '',
        serialNum: '',
        purchaseDate: ''
      }
    },
    postFormData () {
      this.touchForm()

      if (this.isValidForm) {
        this.isFormLoading = true

        const formData = {}
        const purchaseDate = new Date(this.formData.purchaseDate)
        let errorMsg = ''
        let successMsg = ''

        if (this.serialFullInfo) {
          const expDate = moment(purchaseDate, 'DD-MM-YYYY').add(this.serialFullInfo.warrantyYear, 'years').toDate()

          formData.serialNumber = this.serialFullInfo.serial
          formData.phone = this.formData.phone
          formData.name = this.formData.name + ' ' + this.formData.lastName
          formData.expDate = expDate
          formData.registrationDate = purchaseDate
          formData.segment = this.formData.purchasePlace
          formData.email = this.formData.email
          formData.typeId = this.formData.typeId

          successMsg = `${this.$t('sn-registration.registeredMessage')}
                        ${this.$t('sn-registration.yourWarranty')} ${this.serialFullInfo.warrantyYear}
                        ${this.$t('sn-registration.yearsTo')}
                        ${moment(expDate).format('DD.MM.YYYY')}`

          errorMsg = `${this.$t('sn-registration.alreadyRegisteredMessage')}
                      ${this.$t('sn-registration.warrantyTo')}
                      ${moment(this.serialFullInfo.warrantyExpirationDate).format('DD.MM.YYYY')}`

          api.serials.postValidFormData(formData)
            .then(() => {
              this.notifySuccess(successMsg)
            })
            .catch(() => {
              this.notifyError(errorMsg)
            })
            .finally(() => {
              this.clearFullForm()
              this.isFormLoading = false
            })
        } else {
          formData.serial = this.formData.serial
          formData.summaryCode = 'unknown'
          formData.barcodeserial = this.formData.serial
          formData.clientName = this.formData.name + ' ' + this.formData.lastName
          formData.clientPhone = this.formData.phone
          formData.docDate = purchaseDate
          formData.email = this.formData.email
          formData.newWarrantyExpirationDate = purchaseDate
          formData.typeId = this.formData.typeId

          successMsg = this.$t('sn-registration.processingMessage')

          api.serials.postUnknownFormData(formData)
            .then(() => {
              this.notifySuccess(successMsg)
            })
            .finally(() => {
              this.clearFullForm()
              this.isFormLoading = false
            })
        }
      }
    },
    notifySuccess (message) {
      this.$notify({
        group: 'success',
        title: '',
        text: message,
        type: 'success'
      })
    },
    notifyError (message) {
      this.$notify({
        group: 'error',
        title: this.$t('error'),
        text: message,
        type: 'error'
      })
    }
  },
  watch: {
    userData: {
      immediate: true,
      handler (val) {
        this.formData.email = val?.email
        this.formData.phone = val?.phone
        this.formData.name = val?.name
        this.formData.lastName = val?.['last_name']
      }
    }
  },
  created () {
    if (router?.history?.current?.query?.tm) {
      const intm = router.history.current.query.tm

      if (intm === 'rozetka') {
        this.formData.typeId = 9
      } else if (intm === 'epic') {
        this.formData.typeId = 11
      }

      this.formData.purchasePlace = intm
    }

    if (window.location.href.indexOf(`rozetka-sn`) > 0) {
      this.formData.typeId = 10
    } else if (window.location.href.indexOf(`27ua-sn`) > 0) {
      this.formData.typeId = 12
    }
  }
}
</script>

<style scoped lang="sass">
.tools-registration-form
  position: relative
  width: 100%

.tools-registration-form__row
  display: flex
  margin: 0 -5px

  &:not(:first-child)
    margin-top: 10px

.tools-registration-form__col
  width: 50%
  padding: 2.5px 5px

  &--flex
    display: flex

  &--full
    width: 100%

.control--serial-firm,
.control--serial-num,
.control--serial-btn
  &:focus
    z-index: 2

.control--error
  z-index: 2

.control--serial-firm
  max-width: 70px
  border-radius: 3px 0 0 3px

.control--serial-num
  border-radius: 0
  margin: 0 -1px

.control--serial-btn
  border-radius: 0 3px 3px 0

.control--btn
  display: block
  margin: 0 auto

.agree-terms__text
  display: block
  color: #3e3e3e
  font-size: 10px
  text-align: center
::v-deep
  .custom-date-picker
    margin: 0 0 5px

</style>
